import * as bc from "lib0/broadcastchannel";
import * as encoding from "lib0/encoding";
import * as syncProtocol from "y-protocols/sync";
import { messageSync } from "y-websocket";
import { mergeUpdates } from "yjs";

/**
 * Mirrors y-websocket `broadcastMessage`: send merged Yjs update over WS + BroadcastChannel.
 * @param {import('y-websocket').WebsocketProvider} provider
 * @param {Uint8Array} update
 */
function broadcastDocUpdate(provider, update) {
  const encoder = encoding.createEncoder();
  encoding.writeVarUint(encoder, messageSync);
  syncProtocol.writeUpdate(encoder, update);
  const buf = encoding.toUint8Array(encoder);
  const ws = provider.ws;
  if (provider.wsconnected && ws && ws.readyState === ws.OPEN) {
    ws.send(buf);
  }
  if (provider.bcconnected) {
    bc.publish(provider.bcChannel, buf, provider);
  }
}

/**
 * Replaces the provider's default per-keystroke `doc` update listener with batched sends.
 * Flushes pending updates before `disconnect()` and on `pagehide` to reduce data loss on fast navigation.
 *
 * @param {import('y-websocket').WebsocketProvider} provider
 * @param {{ debounceMs?: number, immediateFlushMinBytes?: number }} [options]
 * @returns {{ flushPendingDocUpdates: () => void }}
 */
export function attachBatchedYjsDocUpdates(provider, options = {}) {
  const debounceMs = options.debounceMs ?? 200;
  const immediateFlushMinBytes = options.immediateFlushMinBytes ?? 4096;

  const doc = provider.doc;
  const originalHandler = provider._updateHandler;

  /** @type {Uint8Array[]} */
  let pending = [];
  /** @type {ReturnType<typeof setTimeout> | null} */
  let flushTimer = null;

  const flushPendingDocUpdates = () => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (pending.length === 0) {
      return;
    }
    const merged = pending.length === 1 ? pending[0] : mergeUpdates(pending);
    pending = [];
    broadcastDocUpdate(provider, merged);
  };

  const scheduleFlush = () => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
    }
    flushTimer = setTimeout(flushPendingDocUpdates, debounceMs);
  };

  /**
   * @param {Uint8Array} update
   * @param {unknown} origin
   */
  const batchedHandler = (update, origin) => {
    if (origin === provider) {
      return;
    }
    pending.push(update);
    if (update.byteLength >= immediateFlushMinBytes) {
      flushPendingDocUpdates();
    } else {
      scheduleFlush();
    }
  };

  doc.off("update", originalHandler);
  doc.on("update", batchedHandler);
  provider._updateHandler = batchedHandler;

  const onPageHide = () => {
    flushPendingDocUpdates();
  };
  window.addEventListener("pagehide", onPageHide);

  const originalDisconnect = provider.disconnect.bind(provider);
  provider.disconnect = () => {
    flushPendingDocUpdates();
    window.removeEventListener("pagehide", onPageHide);
    originalDisconnect();
  };

  return { flushPendingDocUpdates };
}